import fetcher from '@/services/fetcher'
import type { Nullable, Plan } from '@/types'
import {
  MembershipPeriods,
  type TMembershipPeriodValue,
  type TPlanTier,
} from '@/utils/helper-objects'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { groupBy } from '@/utils/utils'
import { useCookies } from '@vueuse/integrations/useCookies.mjs'
import { defineStore } from 'pinia'

import { useUserStore } from './user'

type PlansStoreState = {
  plans: Map<string, Plan>
  legacyPlans: Map<string, Plan>
  upsellPlans: Map<string, Plan>
  affiliatePlans: Map<string, Plan>
  selectedMembershipPeriod: TMembershipPeriodValue | 'postal'
}

const upgradePaths: Record<TPlanTier, TPlanTier[]> = {
  postal: [],
  lite: ['standard', 'platinum', 'vip'],
  standard: ['platinum', 'vip'],
  'standard-plus': ['platinum', 'vip'],
  unlimited: ['platinum'],
  platinum: ['vip'],
  gold: ['vip'],
  vip: [],
} as const

export const usePlansStore = defineStore('plans', {
  state: (): PlansStoreState => ({
    plans: new Map(),
    legacyPlans: new Map(),
    upsellPlans: new Map(),
    affiliatePlans: new Map(),
    selectedMembershipPeriod: MembershipPeriods.Monthly,
  }),

  actions: {
    async fetchPlans(): Promise<PlansStoreState['plans']> {
      // if (this.plans.size) {
      //   return this.plans
      // }

      try {
        await fetcher.get<Plan>('/data/plans.json', false, {
          key: 'code',
          map: this.plans,
        })
      } catch {
        // TODO: Add Datadog custom log
      }

      return this.plans
    },
    async fetchLegacyPlans(): Promise<PlansStoreState['legacyPlans']> {
      // if (this.plans.size) {
      //   return this.plans
      // }

      try {
        await fetcher.get<Plan>('/data/legacy-plans.json', false, {
          key: 'id',
          map: this.legacyPlans,
        })
      } catch {
        // TODO: Add Datadog custom log
      }

      return this.legacyPlans
    },
    async fetchUpsellPlans(): Promise<PlansStoreState['upsellPlans']> {
      try {
        await fetcher.get<Plan>('/data/upsell-plans.json', false, {
          key: 'code',
          map: this.upsellPlans,
        })
      } catch {
        // TODO: Add Datadog custom log
      }

      return this.upsellPlans
    },
    async fetchAffiliatePlans(): Promise<PlansStoreState['affiliatePlans']> {
      try {
        await fetcher.get<Plan>('/data/affiliate-plans.json', false, {
          key: 'code',
          map: this.affiliatePlans,
        })
      } catch {
        // TODO: Add Datadog custom log
      }

      return this.affiliatePlans
    },
    async fetchAllPlans() {
      await Promise.all([
        this.fetchPlans(),
        this.fetchLegacyPlans(),
        this.fetchUpsellPlans(),
        this.fetchAffiliatePlans(),
      ])
    },
    getPlan(planCode: string, isAffiliatePlan: number = 0): Nullable<Plan> {
      if (isAffiliatePlan) {
        return this.affiliatePlans.get(planCode) ?? null
      }

      // Try direct map lookups first (fast path)
      const direct =
        this.plans.get(planCode) ??
        this.legacyPlans.get(planCode) ??
        this.upsellPlans.get(planCode) ??
        null

      if (direct) {
        return direct
      }

      // Fallback: search across all loaded plans by code or id
      // This covers legacy-plans where the map is keyed by id but we receive a code,
      // and ensures affiliate plans can still be matched when requested
      const lower = planCode.toLowerCase()
      const searchPool = isAffiliatePlan
        ? this.affiliatePlansArray
        : this.allPlansArray

      for (const plan of searchPool) {
        const codeMatch =
          typeof plan.code === 'string' && plan.code.toLowerCase() === lower
        const idMatch =
          typeof plan.id === 'string' && plan.id.toLowerCase() === lower

        if (codeMatch || idMatch) {
          return plan
        }
      }

      return null
    },
    getPlanByTier(
      planTier: string,
      period: TMembershipPeriodValue = MembershipPeriods.Monthly
    ): Nullable<Plan> {
      return (
        this.plansArray.find(
          (p: Plan) =>
            p.tier.toLowerCase() === planTier.toLowerCase() &&
            p.period.toLowerCase() === period.toLowerCase()
        ) ?? null
      )
    },
    legacyPlansByCodeArray(planCode: string): Array<Plan> {
      const allPlans = [...this.legacyPlans.values(), ...this.plans.values()]

      // First try exact ID match (for cases like 'vip-membership-50')
      const exactMatches = allPlans.filter(
        (plan: Plan) =>
          plan.id && plan.id.toLowerCase() === planCode.toLowerCase()
      )

      if (exactMatches.length > 0) {
        return exactMatches
      }

      // Fall back to code contains search (for cases like 'membership-20')
      return allPlans.filter((plan: Plan) =>
        plan.code.toLowerCase().includes(planCode.toLowerCase())
      )
    },

    getAffiliatePlanByTier(
      planTier: string,
      period: TMembershipPeriodValue = MembershipPeriods.Monthly
    ): Nullable<Plan> {
      return (
        this.affiliatePlansArray.find(
          (p: Plan) => p.tier === planTier && p.period === period
        ) ?? null
      )
    },
    async getUpgradePlans(purchasedPlanCode: string): Promise<Plan[]> {
      // Return empty array if upsellPlans is not set or empty
      // We can fetch the upsellPlans from the server here if we want
      // First ensure upsell plans are loaded
      if (this.upsellPlans.size === 0) {
        await this.fetchUpsellPlans()
      }
      const purchasedPlan = this.getPlan(purchasedPlanCode)
      const allowedTiers = purchasedPlan?.tier
        ? upgradePaths[purchasedPlan.tier]
        : []

      return Array.from(this.upsellPlans.values()).filter((p: Plan) =>
        allowedTiers.includes(p.tier)
      )
    },
    async upgrade(
      upgradePlan: Plan,
      userPlan: Plan,
      type: 'upsell' | 'upgrade' = 'upgrade'
    ): Promise<{ success: boolean; alreadyOnPlan: boolean }> {
      const userStore = useUserStore()

      const targetPlanCode = upgradePlan.code?.toLowerCase() ?? null
      const currentPlanCode = userStore.highestPlan?.code?.toLowerCase() ?? null
      // Short-circuit if user is already on this plan
      if (
        targetPlanCode &&
        currentPlanCode &&
        targetPlanCode === currentPlanCode
      ) {
        return { success: true, alreadyOnPlan: true }
      }

      let url = ''

      if (type === 'upsell') {
        url = `subscriptions/${upgradePlan.tier}-upsell/upgrade/`
      } else {
        url = `subscriptions/${upgradePlan.tier}-${upgradePlan.period}/upgrade/`
      }

      const { error } = await fetcher.post(
        toPortalUrl(url),
        { purchased_plan_code: userPlan.code },
        true
      )

      if (error.value) {
        throw error.value
      }

      return { success: true, alreadyOnPlan: false }
    },
    async endTrial() {
      const userStore = useUserStore()

      const cookies = useCookies(['authToken'], {
        autoUpdateDependencies: true,
      })
      const authToken = cookies.get('authToken')

      if (!userStore.user || !authToken) {
        console.error('User must be logged in to end trial')
        return
      }

      const { data, error } = await fetcher.post(
        toPortalUrl('/customers/endTrial'),
        {},
        true
      )

      if (error.value || !data.value) {
        throw new Error(
          error.value?.message ??
            `Unable to end your trial, please contact support.`
        )
      }

      return true
    },
  },

  getters: {
    plansArray(): Array<Plan> {
      return this.plans.size > 0 ? Array.from(this.plans.values()) : []
    },
    annualPlans(): Array<Plan> {
      return [...this.plans.values()].filter((plan: Plan) =>
        plan.period.includes(MembershipPeriods.Yearly)
      )
    },
    monthlyPlans(): Array<Plan> {
      return [...this.plans.values()].filter((plan: Plan) =>
        plan.period.includes(MembershipPeriods.Monthly)
      )
    },
    legacyPlansArray(): Array<Plan> {
      return this.legacyPlans.size > 0 ? Array.from(this.plans.values()) : []
    },
    affiliatePlansArray(): Array<Plan> {
      return this.affiliatePlans.size > 0
        ? Array.from(this.affiliatePlans.values())
        : []
    },
    allPlansArray(): Array<Plan> {
      const totalPlansCount =
        this.plans.size +
        this.legacyPlans.size +
        this.upsellPlans.size +
        this.affiliatePlans.size

      if (totalPlansCount === 0) {
        return []
      }

      return [
        ...this.plans.values(),
        ...this.legacyPlans.values(),
        ...this.upsellPlans.values(),
        ...this.affiliatePlans.values(),
      ]
    },
    plansGroupedByType(): Record<string, Array<Plan>> {
      return groupBy((p: Plan) => p.tier, this.plansArray)
    },
  },
})
